/* eslint-disable react-hooks/exhaustive-deps */
import VirtualizedTable from './VirtualizedTable';
import { CloseCircle, CloseIcon, SearchIcon } from '@/assets/svg';
import Api from '@/helpers/Api';
import { debounced } from '@/helpers/CommonHelper';
import { Box, CircularProgress } from '@material-ui/core';
import Button from '@material-ui/core/Button';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import Divider from '@material-ui/core/Divider';
import Grid from '@material-ui/core/Grid';
import IconButton from '@material-ui/core/IconButton';
import InputAdornment from '@material-ui/core/InputAdornment';
import TextField from '@material-ui/core/TextField';
import Typography from '@material-ui/core/Typography';
import { withStyles } from '@material-ui/core/styles';
import { ArrowBackIos } from '@material-ui/icons';
import { get } from 'lodash';
import isEmpty from 'lodash/isEmpty';
import React, { useState, useEffect, memo, useCallback, useMemo } from 'react';

const PAGING_OPTIONS = {
  pageIndex: 0,
  pageSize: 20,
};

const ITEM_HEIGHT = 60; // Chiều cao mỗi row
const CONTAINER_HEIGHT = 400; // Chiều cao container

const titleStyles = (theme) => ({
  root: {
    margin: 0,
    padding: theme.spacing(2),
  },
  text: {
    fontFamily: 'Lato',
    fontWeight: 700,
  },
  closeButton: {
    position: 'absolute',
    right: theme.spacing(1),
    top: 14,
    color: theme.palette.grey[500],
  },
});

const CustomDialogTitle = withStyles(titleStyles)((props) => {
  const { children, classes, onClose, ...other } = props;
  return (
    <DialogTitle disableTypography className={classes.root} {...other}>
      <Typography variant="h6" className={classes.text}>
        {children}
      </Typography>
      {onClose ? (
        <IconButton
          aria-label="close"
          className={classes.closeButton}
          onClick={onClose}
        >
          <CloseIcon />
        </IconButton>
      ) : null}
    </DialogTitle>
  );
});

const CustomDialog = withStyles({
  paper: {
    borderRadius: 16,
    minHeight: 500,
  },
  paperWidthMd: {
    maxWidth: 800,
    overflowX: 'hidden',
  },
})(Dialog);

const CustomButton = withStyles({
  root: {
    width: 166,
    height: 40,
    borderRadius: 8,

    fontSize: 18,
    fontWeight: 400,
    textTransform: 'unset',
  },
  containedPrimary: {
    boxShadow: 'none',
  },
})(Button);

const CustomDialogContent = withStyles((theme) => ({
  root: {
    padding: 0,
    overflowY: 'hidden',
  },
}))(DialogContent);

const CustomDialogActions = withStyles((theme) => ({
  root: {
    margin: 0,
    padding: theme.spacing(2),
    justifyContent: 'space-between',
  },
}))(DialogActions);

const CustomSearchInput = withStyles((theme) => ({
  root: {
    padding: theme.spacing(2),
    '& .MuiOutlinedInput-root': {
      borderRadius: 8,
    },

    '& input': {
      minHeight: 40,
      padding: 0,
    },
  },
}))(TextField);

const CustomViewButton = withStyles((theme) => ({
  root: {
    textTransform: 'unset',
    fontFamily: 'Lato',
    fontWeight: 700,
    fontSize: 18,
  },
}))(Button);

const CustomViewTitle = withStyles((theme) => ({
  root: {
    textTransform: 'unset',
    fontFamily: 'Lato',
    fontWeight: 700,
    fontSize: 18,
  },
}))(Typography);

const CustomAutocompleteModal = ({
  open,
  onClose,
  submitLabel,
  onSubmit,
  title,
  searchPlaceholder = 'Search by...',
  columns,
  selectedItem,
  renderRow,
  isViewModal,
  setIsViewModal,
  ViewModal,

  endpoint,
  method = 'get',
  defaultFilter,
  hasPayload = true,
  onDataLoaded,
  data,
  setData,
  modalSelectedItems,
  setModalSelectedItems,
}) => {
  const [localSelected, setLocalSelected] = useState([]);

  const [loading, setLoading] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [pageOptions, setPageOptions] = useState(PAGING_OPTIONS);
  const [totalItem, setTotalItem] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [isAutoLoading, setIsAutoLoading] = useState(false);

  // Track previous localSelected to detect actual selection changes
  const prevLocalSelectedRef = React.useRef([]);

  // Filter options to exclude already selected items
  const filteredOptions = useMemo(() => {
    if (!selectedItem || !Array.isArray(selectedItem)) {
      return data;
    }
    return data.filter((item) => !selectedItem.includes(item?.id));
  }, [data, selectedItem]);

  useEffect(() => {
    if (!open) {
      setLocalSelected([]);
      setInputValue('');

      setData([]);
      setPageOptions(PAGING_OPTIONS);
      setHasMore(true);
      // Reset modalSelectedItems when modal closes
      setModalSelectedItems && setModalSelectedItems([]);
      // Reset ref when modal closes
      prevLocalSelectedRef.current = [];
    }
  }, [open, setModalSelectedItems]);

  useEffect(() => {
    if (filteredOptions.length < 10 && hasMore && !loading && !isAutoLoading) {
      setIsAutoLoading(true);
      loadMoreResults().finally(() => setIsAutoLoading(false));
    }
  }, [filteredOptions, hasMore, loading, isAutoLoading]);

  // Update modalSelectedItems when localSelected changes (not when data changes)
  useEffect(() => {
    if (setModalSelectedItems) {
      // Check if localSelected actually changed (compare arrays)
      const hasChanged =
        localSelected.length !== prevLocalSelectedRef.current.length ||
        localSelected.some(
          (id) => !prevLocalSelectedRef.current.includes(id)
        ) ||
        prevLocalSelectedRef.current.some((id) => !localSelected.includes(id));

      if (hasChanged) {
        // Get current modalSelectedItems using functional update
        setModalSelectedItems((prevItems = []) => {
          // Find newly selected IDs
          const newSelectedIds = localSelected.filter(
            (id) => !prevItems.some((item) => item.id === id)
          );

          // Find objects for newly selected IDs from current data
          const newSelectedObjects = newSelectedIds
            .map((id) => data.find((item) => item.id === id))
            .filter((item) => item !== undefined);

          // Remove items that are no longer selected
          const updatedItems = prevItems
            .filter((item) => localSelected.includes(item.id))
            .concat(newSelectedObjects);

          return updatedItems;
        });

        prevLocalSelectedRef.current = localSelected;
      }
    }
  }, [localSelected, data, setModalSelectedItems]);

  useEffect(() => {
    if (!open) return;

    // Reset pagination when search changes
    const resetPagination = {
      pageIndex: 0,
      pageSize: PAGING_OPTIONS.pageSize,
    };
    setPageOptions(resetPagination);
    setData([]);
    setHasMore(true);

    !loading && setLoading(true);
    debounced(async () => {
      try {
        const { result } = await Api[method](
          endpoint,
          hasPayload && {
            ...defaultFilter,
            ...resetPagination,
            search: inputValue,
          }
        );

        const newOptions = get(result, 'list', []);
        const totalItems = get(result, 'totalItem', 0);

        setData(newOptions);
        setTotalItem(totalItems);
        setHasMore(newOptions.length < totalItems);
        onDataLoaded && onDataLoaded(newOptions);
      } catch (error) {
        console.error('Error loading data:', error);
      } finally {
        setLoading(false);
      }
    }, 500);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inputValue, open]);

  const loadMoreResults = useCallback(async () => {
    if (totalItem === data.length || loading) return;

    setLoading(true);
    const nextPageIndex = pageOptions.pageIndex + 1;
    const newPageOptions = {
      ...pageOptions,
      pageIndex: nextPageIndex,
    };

    try {
      const { result } = await Api[method](
        endpoint,
        hasPayload && {
          ...defaultFilter,
          ...newPageOptions,
          search: inputValue,
        }
      );

      const newOptions = get(result, 'list', []);

      setData((prevOptions) => [...prevOptions, ...newOptions]);
      setPageOptions(newPageOptions);
      setHasMore(
        newOptions.length === pageOptions.pageSize &&
          data.length + newOptions.length < totalItem
      );
      onDataLoaded && onDataLoaded([...data, ...newOptions]);
    } catch (error) {
      console.error('Error loading more results:', error);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [totalItem, data.length, loading, pageOptions, hasPayload, inputValue]);

  const handleSubmit = useCallback(() => {
    onSubmit(localSelected);
  }, [onSubmit, localSelected]);

  const handleClose = useCallback(() => {
    onClose();
    setIsViewModal && setIsViewModal(false);
  }, [onClose, setIsViewModal]);

  return (
    <CustomDialog
      maxWidth="md"
      fullWidth
      scroll="body"
      disableEnforceFocus
      open={open}
      onClose={handleClose}
    >
      <CustomDialogTitle onClose={handleClose}>
        {isViewModal ? (
          <>
            <CustomViewButton
              onClick={() => setIsViewModal(false)}
              startIcon={<ArrowBackIos />}
            >
              <CustomViewTitle>View {title} details</CustomViewTitle>
            </CustomViewButton>
          </>
        ) : (
          `${submitLabel} ${title}`
        )}
      </CustomDialogTitle>
      <Divider light />
      <CustomDialogContent>
        {isViewModal ? (
          <ViewModal />
        ) : (
          <Grid container>
            <Grid item xs={12}>
              <CustomSearchInput
                fullWidth
                variant="outlined"
                placeholder={searchPlaceholder}
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                InputProps={{
                  endAdornment: (
                    <>
                      <InputAdornment position="end">
                        <IconButton
                          style={{
                            visibility: !isEmpty(inputValue)
                              ? 'visible'
                              : 'hidden',
                          }}
                          onClick={() => setInputValue('')}
                        >
                          <CloseCircle
                            height={20}
                            width={20}
                            style={{
                              color: '#666666',
                            }}
                          />
                        </IconButton>
                      </InputAdornment>
                      <InputAdornment position="end">
                        <SearchIcon />
                      </InputAdornment>
                    </>
                  ),
                }}
              />
            </Grid>

            <Grid item xs={12}>
              <div style={{ height: CONTAINER_HEIGHT }}>
                {loading && data.length === 0 ? (
                  <Box
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    height="100%"
                  >
                    <CircularProgress color="primary" size={40} />
                  </Box>
                ) : (
                  <VirtualizedTable
                    data={filteredOptions}
                    columns={columns}
                    renderRow={renderRow}
                    selected={localSelected}
                    onSelectChange={setLocalSelected}
                    onUnSelectAll={() => setLocalSelected([])}
                    height={CONTAINER_HEIGHT}
                    itemHeight={ITEM_HEIGHT}
                    hasMore={hasMore}
                    loadMore={loadMoreResults}
                    loading={loading}
                  />
                )}
              </div>
            </Grid>
          </Grid>
        )}
      </CustomDialogContent>
      <Divider light />
      {!isViewModal && (
        <CustomDialogActions>
          <Typography>{localSelected.length} selected</Typography>
          <CustomButton
            onClick={handleSubmit}
            variant="contained"
            color="primary"
            disabled={isEmpty(localSelected)}
          >
            {submitLabel}
          </CustomButton>
        </CustomDialogActions>
      )}
    </CustomDialog>
  );
};

export default memo(CustomAutocompleteModal);
